import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { format, startOfMonth, endOfMonth, eachMonthOfInterval, subMonths, addMonths } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { TrendingUp, TrendingDown, Wallet, Target, Calendar, DollarSign } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CustomTooltipProps, TooltipPayload } from "@/types/recharts";
import { getEffectiveTransactionDate } from '@/lib/utils';
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";

interface TransactionWithCategory {
  id: string;
  tipo: "entrada" | "saida";
  valor: number;
  data: string;
  foi_paga: boolean;
  ignorar_transacao: boolean;
  categoria?: {
    nome: string;
    cor: string;
  };
}

interface NetWorthReportProps {
  transactions: TransactionWithCategory[];
  selectedDate?: Date;
}

interface NetWorthData {
  month: string;
  fullMonth: string;
  receitas: number;
  despesas: number;
  saldoMensal: number;
  patrimonioAcumulado: number;
  receitasAcumuladas: number;
  despesasAcumuladas: number;
}

export function NetWorthReport({ transactions, selectedDate }: NetWorthReportProps) {
  const [viewMode, setViewMode] = useState<'accumulated' | 'monthly'>('accumulated');
  const [timeRange, setTimeRange] = useState(12); // meses
  const [showProjection, setShowProjection] = useState(false);
  const [yearsToShow, setYearsToShow] = useState(2);
  const [showAllYears, setShowAllYears] = useState(false);

  // Buscar contas para calcular datas efetivas
  const { data: accounts = [] } = useQuery({
    queryKey: ['contas'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('contas')
        .select('*')
        .eq('ativo', true);
      
      if (error) throw error;
      return data || [];
    }
  });

  const netWorthData = useMemo(() => {
    if (!transactions || transactions.length === 0) {
      return { monthlyData: [], insights: [], projectionData: [] };
    }

    const endDate = selectedDate ? endOfMonth(selectedDate) : endOfMonth(new Date());
    
    // Calcular período baseado na seleção
    let startDate: Date;
    if (showAllYears) {
      // Filtrar transações válidas para determinar o período
      const validTransactions = transactions.filter(t => !t.ignorar_transacao);
      if (validTransactions.length > 0) {
        const oldestTransaction = validTransactions.reduce((oldest, current) => {
          return new Date(current.data) < new Date(oldest.data) ? current : oldest;
        });
        const oldestYear = new Date(oldestTransaction.data).getFullYear();
        startDate = new Date(oldestYear, 0, 1); // 1º de janeiro do ano mais antigo
      } else {
        startDate = new Date(endDate.getFullYear(), 0, 1); // 1º de janeiro do ano atual
      }
    } else {
      startDate = subMonths(endDate, timeRange - 1);
    }
    
    const months = eachMonthOfInterval({ start: startDate, end: endDate });

    // Filtrar transações válidas
    const validTransactions = transactions.filter(t => !t.ignorar_transacao);

    let patrimonioAcumulado = 0;
    let receitasAcumuladas = 0;
    let despesasAcumuladas = 0;

    // Calcular patrimônio inicial (antes do período)
    const initialTransactions = validTransactions.filter(t => {
      // @ts-expect-error - Transaction type mismatch from database
      const transactionDate = getEffectiveTransactionDate(t, accounts);
      return transactionDate < startDate;
    });

    const initialReceitas = initialTransactions
      .filter(t => t.tipo === 'entrada' && t.foi_paga)
      .reduce((sum, t) => sum + Number(t.valor), 0);

    const initialDespesas = initialTransactions
      .filter(t => t.tipo === 'saida')
      .reduce((sum, t) => sum + Number(t.valor), 0);

    patrimonioAcumulado = initialReceitas - initialDespesas;
    receitasAcumuladas = initialReceitas;
    despesasAcumuladas = initialDespesas;

    // Dados mensais
    const monthlyData: NetWorthData[] = months.map(month => {
      // const monthStart = startOfMonth(month); // Não usado com a nova lógica
      // const monthEnd = endOfMonth(month); // Não usado com a nova lógica
      
      const monthTransactions = validTransactions.filter(t => {
        // @ts-expect-error - Transaction type mismatch from database
        const transactionDate = getEffectiveTransactionDate(t, accounts);
        // Usar comparação de mês e ano para maior robustez
        if (!(transactionDate instanceof Date) || isNaN(transactionDate.getTime())) return false;
        return transactionDate.getMonth() === month.getMonth() && transactionDate.getFullYear() === month.getFullYear();
      });
      
      const receitas = monthTransactions
        .filter(t => t.tipo === 'entrada' && t.foi_paga)
        .reduce((sum, t) => sum + Number(t.valor), 0);
      
      const despesas = monthTransactions
        .filter(t => t.tipo === 'saida')
        .reduce((sum, t) => sum + Number(t.valor), 0);
      
      const saldoMensal = receitas - despesas;
      patrimonioAcumulado += saldoMensal;
      receitasAcumuladas += receitas;
      despesasAcumuladas += despesas;
      
      return {
        month: format(month, 'MMM', { locale: ptBR }),
        fullMonth: format(month, 'MMMM yyyy', { locale: ptBR }),
        receitas,
        despesas,
        saldoMensal,
        patrimonioAcumulado,
        receitasAcumuladas,
        despesasAcumuladas
      };
    });

    // Projeção para os próximos 6 meses
    const projectionData: NetWorthData[] = [];
    if (showProjection && monthlyData.length > 0) {
      const lastThreeMonths = monthlyData.slice(-3);
      const avgReceitas = lastThreeMonths.reduce((sum, m) => sum + m.receitas, 0) / 3;
      const avgDespesas = lastThreeMonths.reduce((sum, m) => sum + m.despesas, 0) / 3;
      const avgSaldo = avgReceitas - avgDespesas;
      
      let projectedPatrimonio = monthlyData[monthlyData.length - 1].patrimonioAcumulado;
      
      for (let i = 1; i <= 6; i++) {
        const projectedMonth = addMonths(endDate, i);
        projectedPatrimonio += avgSaldo;
        
        projectionData.push({
          month: format(projectedMonth, 'MMM', { locale: ptBR }),
          fullMonth: format(projectedMonth, 'MMMM yyyy', { locale: ptBR }) + ' (Projeção)',
          receitas: avgReceitas,
          despesas: avgDespesas,
          saldoMensal: avgSaldo,
          patrimonioAcumulado: projectedPatrimonio,
          receitasAcumuladas: 0,
          despesasAcumuladas: 0
        });
      }
    }

    // Calcular insights
    const currentPatrimonio = monthlyData[monthlyData.length - 1]?.patrimonioAcumulado || 0;
    const previousPatrimonio = monthlyData[monthlyData.length - 2]?.patrimonioAcumulado || 0;
    const patrimonioChange = currentPatrimonio - previousPatrimonio;
    const patrimonioChangePercent = previousPatrimonio !== 0 ? (patrimonioChange / Math.abs(previousPatrimonio)) * 100 : 0;
    
    const avgMonthlySavings = monthlyData.reduce((sum, m) => sum + m.saldoMensal, 0) / monthlyData.length;
    const bestMonth = monthlyData.reduce((best, current) => 
      current.saldoMensal > best.saldoMensal ? current : best
    );
    const worstMonth = monthlyData.reduce((worst, current) => 
      current.saldoMensal < worst.saldoMensal ? current : worst
    );

    const insights = [
      {
        title: 'Patrimônio Atual',
        value: currentPatrimonio,
        type: 'currency' as const,
        change: patrimonioChange,
        changePercent: patrimonioChangePercent,
        icon: Wallet
      },
      {
        title: 'Economia Média Mensal',
        value: avgMonthlySavings,
        type: 'currency' as const,
        trend: avgMonthlySavings > 0 ? 'positive' : 'negative',
        icon: Target
      },
      {
        title: 'Melhor Mês',
        value: bestMonth.saldoMensal,
        type: 'currency' as const,
        month: bestMonth.fullMonth,
        icon: TrendingUp
      },
      {
        title: 'Pior Mês',
        value: worstMonth.saldoMensal,
        type: 'currency' as const,
        month: worstMonth.fullMonth,
        icon: TrendingDown
      }
    ];

    return { monthlyData, insights, projectionData };
  }, [transactions, selectedDate, timeRange, showProjection, showAllYears, accounts]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatPercentage = (value: number) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
  };

  const CustomTooltip = ({ active, payload, label }: CustomTooltipProps) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-background border border-border rounded-lg p-3 shadow-lg">
          <p className="font-medium mb-2">{label}</p>
          {payload.map((entry: TooltipPayload, index: number) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name || entry.dataKey}: {formatCurrency(entry.value as number)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const chartData = showProjection ? 
    [...netWorthData.monthlyData, ...netWorthData.projectionData] : 
    netWorthData.monthlyData;

  return (
    <div className="space-y-6">
      {/* Insights Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {netWorthData.insights.map((insight, index) => {
          const IconComponent = insight.icon;
          return (
            <Card key={insight.title}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-base font-medium text-muted-foreground">
            {insight.title}
          </CardTitle>
                <div className={`p-2 rounded-md ${
                  insight.trend === 'positive' ? 'bg-success/10 text-success' :
                  insight.trend === 'negative' ? 'bg-destructive/10 text-destructive' :
                  'bg-primary/10 text-primary'
                }`}>
                  <IconComponent className="h-4 w-4" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-base font-bold">
                  {formatCurrency(insight.value)}
                </div>
                <div className="flex items-center gap-2 mt-1">
                  {insight.change !== undefined && (
                    <Badge variant={insight.change >= 0 ? 'default' : 'destructive'}>
                      {insight.change >= 0 ? '+' : ''}{formatCurrency(insight.change)}
                    </Badge>
                  )}
                  {insight.changePercent !== undefined && (
                    <span className={`text-xs ${
                      insight.changePercent >= 0 ? 'text-success' : 'text-destructive'
                    }`}>
                      {formatPercentage(insight.changePercent)}
                    </span>
                  )}
                  {insight.month && (
                    <span className="text-xs text-muted-foreground">
                      {insight.month}
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex gap-2">
          <Button
            variant={viewMode === 'accumulated' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('accumulated')}
          >
            <Wallet className="h-4 w-4 mr-2" />
            Acumulado
          </Button>
          <Button
            variant={viewMode === 'monthly' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('monthly')}
          >
            <Calendar className="h-4 w-4 mr-2" />
            Mensal
          </Button>
        </div>
        
        <div className="flex gap-2 items-center">
          <Select 
            value={showAllYears ? "all" : (timeRange >= 24 ? `${yearsToShow}y` : timeRange.toString())} 
            onValueChange={(value) => {
              if (value === "all") {
                setShowAllYears(true);
              } else if (value.endsWith('y')) {
                setShowAllYears(false);
                const years = Number(value.replace('y', ''));
                setYearsToShow(years);
                setTimeRange(years * 12);
              } else {
                setShowAllYears(false);
                setTimeRange(Number(value));
              }
            }}
          >
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="6">6 meses</SelectItem>
              <SelectItem value="12">12 meses</SelectItem>
              <SelectItem value="18">18 meses</SelectItem>
                <SelectItem value="2y">2 anos</SelectItem>
              <SelectItem value="3y">3 anos</SelectItem>
              <SelectItem value="4y">4 anos</SelectItem>
              <SelectItem value="5y">5 anos</SelectItem>
              <SelectItem value="all">Todos os anos</SelectItem>
            </SelectContent>
          </Select>
          
          <Button
            variant={showProjection ? 'default' : 'outline'}
            size="sm"
            onClick={() => setShowProjection(!showProjection)}
          >
            <Target className="h-4 w-4 mr-2" />
            Projeção
          </Button>
        </div>
      </div>

      {/* Main Chart */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            {viewMode === 'accumulated' ? 'Evolução do Patrimônio' : 'Fluxo Mensal'}
            {showProjection && <Badge variant="outline">Com Projeção</Badge>}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              {viewMode === 'accumulated' ? (
                <AreaChart data={chartData} margin={{ top: 20, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="patrimonioGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--chart-2))" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="hsl(var(--chart-2))" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis 
                    dataKey="month" 
                    tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                  />
                  <YAxis 
                    tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                    tickFormatter={(value) => {
                      if (Math.abs(value) >= 1000) {
                        return `R$ ${(value / 1000).toFixed(0)}k`;
                      }
                      return `R$ ${value}`;
                    }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="patrimonioAcumulado"
                    stroke="hsl(var(--chart-2))"
                    fillOpacity={1}
                    fill="url(#patrimonioGradient)"
                    strokeWidth={2}
                    name="Patrimonio"
                  />
                  {showProjection && (
                    <Area
                      type="monotone"
                      dataKey="patrimonioAcumulado"
                      stroke="hsl(var(--chart-3))"
                      strokeDasharray="5 5"
                      fill="none"
                      strokeWidth={2}
                      name="Patrimonio (Projeção)"
                    />
                  )}
                </AreaChart>
              ) : (
                <LineChart data={chartData} margin={{ top: 20, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis 
                    dataKey="month" 
                    tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                  />
                  <YAxis 
                    tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                    tickFormatter={(value) => {
                      if (Math.abs(value) >= 1000) {
                        return `R$ ${(value / 1000).toFixed(0)}k`;
                      }
                      return `R$ ${value}`;
                    }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Line
                    type="monotone"
                    dataKey="receitas"
                    stroke="hsl(var(--chart-2))"
                    strokeWidth={2}
                    dot={{ fill: 'hsl(var(--chart-2))', strokeWidth: 2, r: 4 }}
                    name="Receitas"
                  />
                  <Line
                    type="monotone"
                    dataKey="despesas"
                    stroke="hsl(var(--chart-1))"
                    strokeWidth={2}
                    dot={{ fill: 'hsl(var(--chart-1))', strokeWidth: 2, r: 4 }}
                    name="Despesas"
                  />
                  <Line
                    type="monotone"
                    dataKey="saldoMensal"
                    stroke="hsl(var(--chart-3))"
                    strokeWidth={2}
                    dot={{ fill: 'hsl(var(--chart-3))', strokeWidth: 2, r: 4 }}
                    name="Saldo Mensal"
                  />
                </LineChart>
              )}
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Summary Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Resumo Detalhado</CardTitle>
        </CardHeader>
        <CardContent>
          {/* Desktop Table */}
          <div className="hidden md:block">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left p-2">Mês</th>
                  <th className="text-right p-2">Receitas</th>
                  <th className="text-right p-2">Despesas</th>
                  <th className="text-right p-2">Saldo</th>
                  <th className="text-right p-2">Patrimônio</th>
                </tr>
              </thead>
              <tbody>
                {chartData.slice(-6).map((data, index) => (
                  <tr key={data.month} className="border-b border-border/50">
                    <td className="p-2 font-medium">
                      {data.fullMonth}
                      {data.fullMonth.includes('Projeção') && (
                        <Badge variant="outline" className="ml-2 text-xs">Proj.</Badge>
                      )}
                    </td>
                    <td className="text-right p-2 text-success">
                      {formatCurrency(data.receitas)}
                    </td>
                    <td className="text-right p-2 text-destructive">
                      {formatCurrency(data.despesas)}
                    </td>
                    <td className={`text-right p-2 font-medium ${
                      data.saldoMensal >= 0 ? 'text-success' : 'text-destructive'
                    }`}>
                      {formatCurrency(data.saldoMensal)}
                    </td>
                    <td className="text-right p-2 font-bold">
                      {formatCurrency(data.patrimonioAcumulado)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Mobile Cards */}
          <div className="md:hidden space-y-4">
            {chartData.slice(-6).map((data, index) => (
              <div key={data.month} className="border border-border rounded-lg p-4">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-medium text-sm">{data.fullMonth}</h4>
                  {data.fullMonth.includes('Projeção') && (
                    <Badge variant="outline" className="text-xs">Proj.</Badge>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <div className="text-muted-foreground mb-1">Receitas</div>
                    <div className="text-success font-medium">{formatCurrency(data.receitas)}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground mb-1">Despesas</div>
                    <div className="text-destructive font-medium">{formatCurrency(data.despesas)}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground mb-1">Saldo Mensal</div>
                    <div className={`font-medium ${
                      data.saldoMensal >= 0 ? 'text-success' : 'text-destructive'
                    }`}>
                      {formatCurrency(data.saldoMensal)}
                    </div>
                  </div>
                  <div>
                    <div className="text-muted-foreground mb-1">Patrimônio</div>
                    <div className="font-bold">{formatCurrency(data.patrimonioAcumulado)}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}